import React, { useState } from 'react';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const initialFormState = {
  full_name: '',
  email: '',
  mobile: '',
  password: '',
  confirmPassword: '',
  gender: '',
  date_of_birth: '',
  class_level: '',
};

const SignupForm = () => {
  const navigate = useNavigate();
  const { signup, verifyEmail, resendVerification } = useAuth();

  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState(initialFormState);
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [isResending, setIsResending] = useState(false);
  const [resendInfo, setResendInfo] = useState({ type: '', message: '' });
  const [showPassword, setShowPassword] = useState({ password: false, confirmPassword: false });

  const today = new Date().toISOString().split('T')[0];

  const handleTogglePassword = (field) => {
    setShowPassword((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const extractFirstMessage = (payload) => {
    if (!payload) return null;
    if (typeof payload === 'string') return payload;
    if (Array.isArray(payload)) {
      const first = payload.find((item) => typeof item === 'string');
      return first || null;
    }
    if (typeof payload === 'object') {
      for (const key of Object.keys(payload)) {
        const value = payload[key];
        const message = extractFirstMessage(value);
        if (message) return message;
      }
    }
    return null;
  };

  const getErrorMessage = (err, fallback) => {
    const responseData = err?.response?.data;
    const structuredMessage = extractFirstMessage(responseData);

    if (responseData?.errors?.email) {
      const emailMessage = extractFirstMessage(responseData.errors.email);
      if (emailMessage && !/status code/i.test(emailMessage)) {
        return emailMessage;
      }
    }

    if (structuredMessage) {
      const trimmedStructured = structuredMessage.trim();
      if (trimmedStructured && !/status code/i.test(trimmedStructured)) {
        return trimmedStructured;
      }
    }

    const rawMessage = err?.response?.data?.detail
      || err?.response?.data?.message
      || err?.response?.data?.error
      || err?.message
      || fallback;

    if (typeof rawMessage !== 'string') {
      return 'Something went wrong. Please try again.';
    }

    const trimmedMessage = rawMessage.trim();

    if (!trimmedMessage) {
      return 'Something went wrong. Please try again.';
    }

    if (/status code/i.test(trimmedMessage)) {
      return 'Something went wrong. Please try again.';
    }

    if (/^network error$/i.test(trimmedMessage)) {
      return 'Network error. Please try again later.';
    }

    const errorDetailMatch = trimmedMessage.match(/ErrorDetail\(string=['"]([^'"]+)['"]/);
    if (errorDetailMatch) {
      return errorDetailMatch[1];
    }

    if (/validation failed/i.test(trimmedMessage)) {
      const extracted = trimmedMessage.split(':').pop()?.trim();
      if (extracted && extracted !== trimmedMessage) {
        return extracted;
      }
    }

    return trimmedMessage;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (step === 1) {
      const trimmedName = formData.full_name.trim();
      const trimmedEmail = formData.email.trim();
      const trimmedMobile = formData.mobile.trim();
      const emailPattern = /^[^\s@]+@[^\s@]+$/;

      if (!trimmedName) {
        setError('Please enter your full name');
        return;
      }

      if (trimmedName.length > 50) {
        setError('Full name must be 50 characters or fewer');
        return;
      }

      if (!trimmedEmail) {
        setError('Please enter a valid email');
        return;
      }

      if (!emailPattern.test(trimmedEmail)) {
        setError('Please enter a valid email address');
        return;
      }

      if (formData.password.length < 8) {
        setError('Password must be at least 8 characters');
        return;
      }

      if (formData.password !== formData.confirmPassword) {
        setError('Passwords do not match');
        return;
      }

      if (!/^[0-9]{10}$/.test(trimmedMobile)) {
        setError('Please enter a valid 10-digit mobile number');
        return;
      }

      if (!formData.gender) {
        setError('Please select a gender');
        return;
      }

      if (!formData.date_of_birth) {
        setError('Please choose your date of birth');
        return;
      }

      if (!formData.class_level) {
        setError('Please select your class');
        return;
      }

      try {
        await signup({
          full_name: trimmedName,
          email: trimmedEmail,
          mobile: trimmedMobile,
          password: formData.password,
          gender: formData.gender,
          date_of_birth: formData.date_of_birth,
          class_level: formData.class_level,
        });
        setError('');
        setCode('');
        setResendInfo({ type: '', message: '' });
        setStep(2);
      } catch (err) {
        setError(getErrorMessage(err, 'Something went wrong. Please try again.'));
      }
    } else if (step === 2) {
      if (!code.trim()) {
        setError('Enter the verification code');
        return;
      }

      try {
        await verifyEmail(formData.email.trim(), code.trim());
        setResendInfo({ type: '', message: '' });
        setStep(3);
      } catch (err) {
        setError(getErrorMessage(err, 'Something went wrong. Please try again.'));
      }
    } else {
      navigate('/');
    }
  };

  const handleResend = async () => {
    const email = formData.email.trim();
    if (!email) {
      setResendInfo({ type: 'error', message: 'Email is missing from the form.' });
      return;
    }

    setError('');
    setResendInfo({ type: '', message: '' });
    setIsResending(true);
    try {
      await resendVerification(email);
      setResendInfo({ type: 'success', message: 'Verification code resent. Check your inbox.' });
    } catch (err) {
      setResendInfo({ type: 'error', message: getErrorMessage(err, 'Could not resend code') });
    } finally {
      setIsResending(false);
    }
  };

  const title = step === 1 ? 'Create account' : step === 2 ? 'Verify Email' : 'All set!';

  return (
    <div className="min-h-screen flex items-center justify-center bg-bg-default py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-primary">{title}</h2>
        </div>

        {error && <div className="p-3 bg-error/10 text-error rounded">{error}</div>}

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {step === 1 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Row1: Name (left) */}
              <div>
                <label className="block text-sm font-medium text-text-secondary" htmlFor="full_name">
                  Full Name<span className="text-error ml-1">*</span>
                </label>
                <input
                  id="full_name"
                  name="full_name"
                  type="text"
                  required
                  className="input-field w-full"
                  placeholder="Full Name"
                  value={formData.full_name}
                  onChange={handleChange}
                  maxLength={50}
                />
              </div>

              {/* Row1: Class (right) */}
              <div>
                <label className="block text-sm font-medium text-text-secondary" htmlFor="class_level">
                  Class<span className="text-error ml-1">*</span>
                </label>
                <select
                  id="class_level"
                  name="class_level"
                  required
                  className="input-field w-full"
                  value={formData.class_level}
                  onChange={handleChange}
                >
                  <option value="">Select class</option>
                  <option value="Class 8">Class 8</option>
                  <option value="Class 9">Class 9</option>
                  <option value="Class 10">Class 10</option>
                  <option value="Class 11">Class 11</option>
                  <option value="Class 12">Class 12</option>
                  <option value="Dropper">Dropper</option>
                </select>
              </div>

              {/* Row2: Email (left) */}
              <div>
                <label className="block text-sm font-medium text-text-secondary" htmlFor="email">
                  Email Address<span className="text-error ml-1">*</span>
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  className="input-field w-full"
                  placeholder="Email Address"
                  value={formData.email}
                  onChange={handleChange}
                  inputMode="email"
                />
              </div>

              {/* Row2: Mobile (right) */}
              <div>
                <label className="block text-sm font-medium text-text-secondary" htmlFor="mobile">
                  Mobile Number<span className="text-error ml-1">*</span>
                </label>
                <input
                  id="mobile"
                  name="mobile"
                  type="tel"
                  required
                  className="input-field w-full"
                  placeholder="10-digit mobile number"
                  value={formData.mobile}
                  onChange={handleChange}
                  maxLength={10}
                  inputMode="numeric"
                />
              </div>

              {/* Row3: Gender (left) */}
              <div>
                <label className="block text-sm font-medium text-text-secondary" htmlFor="gender">
                  Gender<span className="text-error ml-1">*</span>
                </label>
                <select
                  id="gender"
                  name="gender"
                  required
                  className="input-field w-full"
                  value={formData.gender}
                  onChange={handleChange}
                >
                  <option value="">Select gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              {/* Row3: DOB (right) */}
              <div>
                <label className="block text-sm font-medium text-text-secondary" htmlFor="date_of_birth">
                  Date of Birth<span className="text-error ml-1">*</span>
                </label>
                <input
                  id="date_of_birth"
                  name="date_of_birth"
                  type="date"
                  required
                  className="input-field w-full"
                  value={formData.date_of_birth}
                  onChange={handleChange}
                  max={today}
                />
              </div>

              {/* Row4: Password (left) */}
              <div>
                <label className="block text-sm font-medium text-text-secondary" htmlFor="password">
                  Password<span className="text-error ml-1">*</span>
                </label>
                <div className="relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword.password ? 'text' : 'password'}
                    required
                    className="input-field w-full pr-12"
                    placeholder="Password"
                    value={formData.password}
                    onChange={handleChange}
                    minLength={8}
                  />
                  <button
                    type="button"
                    tabIndex={-1}
                    onClick={() => handleTogglePassword('password')}
                    className="absolute inset-y-0 right-3 flex items-center justify-center bg-transparent border-none p-0"
                    aria-label={showPassword.password ? 'Hide password' : 'Show password'}
                  >
                    {showPassword.password ? <FiEyeOff size={22} /> : <FiEye size={22} />}
                  </button>
                </div>
              </div>

              {/* Row4: Confirm Password (right) */}
              <div>
                <label className="block text-sm font-medium text-text-secondary" htmlFor="confirmPassword">
                  Confirm Password<span className="text-error ml-1">*</span>
                </label>
                <div className="relative">
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showPassword.confirmPassword ? 'text' : 'password'}
                    required
                    className="input-field w-full pr-12"
                    placeholder="Confirm Password"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    minLength={8}
                  />
                  <button
                    type="button"
                    tabIndex={-1}
                    onClick={() => handleTogglePassword('confirmPassword')}
                    className="absolute inset-y-0 right-3 flex items-center justify-center bg-transparent border-none p-0"
                    aria-label={showPassword.confirmPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword.confirmPassword ? <FiEyeOff size={22} /> : <FiEye size={22} />}
                  </button>
                </div>
              </div>

              {/* Row5: Submit button (full width) */}
              <div className="md:col-span-2 mt-4">
                <button type="submit" className="btn-primary w-full h-12 text-base">
                  Sign Up
                </button>
              </div>
            </div>
          ) : null}

          {step === 2 ? (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-text-secondary" htmlFor="verificationCode">
                  Verification Code<span className="text-error ml-1">*</span>
                </label>
                <input
                  id="verificationCode"
                  className="input-field md:max-w-sm w-full"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  maxLength={6}
                  inputMode="numeric"
                  placeholder="Enter code"
                />
                <p className="mt-2 text-sm text-text-secondary">Didn’t get the email? Check your spam or promotions folder.</p>
              </div>
              <div className="flex flex-col gap-2 md:max-w-sm">
                <button
                  type="button"
                  className="btn-secondary w-full h-12 text-base"
                  onClick={handleResend}
                  disabled={isResending}
                >
                  {isResending ? 'Resending…' : 'Resend code'}
                </button>
                {resendInfo.message && (
                  <div className={`text-sm ${resendInfo.type === 'success' ? 'text-success' : 'text-error'}`}>
                    {resendInfo.message}
                  </div>
                )}
                <button
                  type="submit"
                  className="btn-primary w-full h-12 text-base"
                >
                  Verify
                </button>
              </div>
            </div>
          ) : null}
        </form>
      </div>
    </div>
  );
};

export default SignupForm;
